import { logger } from './logger';

/**
 * Bunny CDN Dynamic Image API Optimizer
 * Provides on-the-fly image optimization and transformation capabilities
 */

export interface BunnyImageOptions {
  width?: number;
  height?: number;
  aspectRatio?: string; // e.g., "16:9", "4:3"
  crop?: string; // "x,y,width,height"
  focusCrop?: string; // "x,y,scaleX,scaleY"
  quality?: number; // 1-100
  format?: 'webp' | 'jpg' | 'png' | 'avif';
  sharpen?: boolean;
  blur?: number; // 0-100
  brightness?: number; // -100 to 100
  contrast?: number; // -100 to 100
  saturation?: number; // -100 to 100
  gamma?: number; // 0-300
  tint?: string; // hex color without #
  flip?: boolean;
  flop?: boolean;
  rotate?: number; // degrees
  grayscale?: boolean;
}

/**
 * Check if Bunny optimization is enabled via environment variables
 */
const isBunnyOptimizationEnabled = (): boolean => {
  return import.meta.env.VITE_BUNNY_OPTIMIZATION_ENABLED === 'true';
};

/**
 * Get Bunny CDN base URL from environment
 */
const getBunnyCDNBaseUrl = (): string => {
  return import.meta.env.VITE_BUNNY_CDN_BASE_URL || '';
};

/**
 * Convert image URL to use Bunny CDN if optimization is enabled
 * Preserves signed URL parameters (token, expires) for authentication
 */
const convertToBunnyCDN = (originalUrl: string): string => {
  if (!isBunnyOptimizationEnabled() || !getBunnyCDNBaseUrl()) {
    return originalUrl;
  }

  const bunnyBaseUrl = getBunnyCDNBaseUrl();

  // Extract the path from the original URL
  try {
    const url = new URL(originalUrl);

    // If URL is already a Bunny CDN URL, remove signed parameters
    if (originalUrl.includes(bunnyBaseUrl)) {
      // Remove token and expires parameters, keep only the clean path
      return `${bunnyBaseUrl}${url.pathname}`;
    }

    // For external URLs, proxy through Bunny CDN
    const path = url.pathname + url.search;
    return `${bunnyBaseUrl}${path}`;
  } catch {
    // If URL parsing fails, try to construct path from relative URL
    const path = originalUrl.startsWith('/') ? originalUrl : `/${originalUrl}`;
    return `${bunnyBaseUrl}${path}`;
  }
};

/**
 * Build query parameters for Bunny Dynamic Image API
 */
const buildBunnyParams = (options: BunnyImageOptions): string => {
  const params = new URLSearchParams();

  if (options.width) params.append('width', options.width.toString());
  if (options.height) params.append('height', options.height.toString());
  if (options.aspectRatio) params.append('aspect_ratio', options.aspectRatio);
  if (options.crop) params.append('crop', options.crop);
  if (options.focusCrop) params.append('focus_crop', options.focusCrop);
  if (options.quality) params.append('quality', options.quality.toString());
  if (options.format) params.append('format', options.format);
  if (options.sharpen) params.append('sharpen', 'true');
  if (options.blur) params.append('blur', options.blur.toString());
  if (options.brightness) params.append('brightness', options.brightness.toString());
  if (options.contrast) params.append('contrast', options.contrast.toString());
  if (options.saturation) params.append('saturation', options.saturation.toString());
  if (options.gamma) params.append('gamma', options.gamma.toString());
  if (options.tint) params.append('tint', options.tint);
  if (options.flip) params.append('flip', 'true');
  if (options.flop) params.append('flop', 'true');
  if (options.rotate) params.append('rotate', options.rotate.toString());
  if (options.grayscale) params.append('grayscale', 'true');

  return params.toString();
};

/**
 * Main function to optimize images using Bunny CDN Dynamic Image API
 * @param originalUrl - The original image URL
 * @param options - Optimization options
 * @returns Optimized image URL or original URL if optimization is disabled
 */
export const optimizeImageWithBunny = (
  originalUrl: string,
  options: BunnyImageOptions = {},
): string => {
  if (!isBunnyOptimizationEnabled() || !originalUrl) {
    return originalUrl;
  }

  try {
    // Convert to Bunny CDN URL (preserves existing parameters for signed URLs)
    const bunnyUrl = convertToBunnyCDN(originalUrl);

    // Build optimization parameters
    const newParams = buildBunnyParams(options);

    if (!newParams) {
      return bunnyUrl;
    }

    // Add optimization parameters to clean URL
    return `${bunnyUrl}?${newParams}`;
  } catch (error) {
    logger.warn('Bunny image optimization failed:', error);
    return originalUrl;
  }
};

/**
 * Preset configurations for common use cases
 */
export const BunnyPresets = {
  // Thumbnail presets
  thumbnail: (width: number = 256): BunnyImageOptions => ({
    width,
    aspectRatio: '16:9',
    quality: 85,
    format: 'webp',
    sharpen: true,
  }),

  // Avatar presets
  avatar: (size: number = 128): BunnyImageOptions => ({
    width: size,
    height: size,
    aspectRatio: '1:1',
    quality: 90,
    format: 'webp',
  }),

  // Hero image presets
  hero: (width: number = 1920): BunnyImageOptions => ({
    width,
    quality: 80,
    format: 'webp',
    sharpen: true,
  }),

  // Mobile optimized
  mobile: (width: number = 768): BunnyImageOptions => ({
    width,
    quality: 75,
    format: 'webp',
  }),

  // High quality for galleries
  gallery: (width: number = 1200): BunnyImageOptions => ({
    width,
    quality: 90,
    format: 'webp',
    sharpen: true,
  }),

  // Square images (1:1 aspect ratio)
  square: (size: number = 256): BunnyImageOptions => ({
    width: size,
    height: size,
    aspectRatio: '1:1',
    quality: 85,
    format: 'webp',
    sharpen: true,
  }),
};

/**
 * Utility functions for responsive images
 */
export const generateResponsiveSrcSet = (
  originalUrl: string,
  widths: number[] = [320, 640, 768, 1024, 1280, 1920],
  options: Omit<BunnyImageOptions, 'width'> = {},
): string => {
  if (!isBunnyOptimizationEnabled()) {
    return originalUrl;
  }

  return widths
    .map((width) => {
      const optimizedUrl = optimizeImageWithBunny(originalUrl, { ...options, width });
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
};

/**
 * Check if Bunny optimization is available
 */
export const isBunnyAvailable = (): boolean => {
  return isBunnyOptimizationEnabled() && !!getBunnyCDNBaseUrl();
};

export default optimizeImageWithBunny;
